
import React from 'react';
import { motion } from 'framer-motion';
import { Home, Heart, Shield, MessageCircle, CheckCircle, ArrowRightCircle, Users, Eye, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const ParentsPage = () => {
  const fadeIn = (delay = 0, y = 20) => ({
    hidden: { opacity: 0, y: y, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.7, delay, ease: [0.6, -0.05, 0.01, 0.99] } },
  });

  const parentConcerns = [
    {
      concern: "Mijn kind durft niet te vertellen wat er speelt",
      solution: "Classbuddy biedt een veilige, oordeel-vrije ruimte waar kinderen vrijuit kunnen praten.",
      icon: <MessageCircle size={24} sm:size={32} className="text-cm-pink" />
    },
    {
      concern: "Ik weet niet wat er op school gebeurt",
      solution: "Buddy's helpen kinderen hun ervaringen te verwerken en moedigen hen aan om met u te praten.",
      icon: <Eye size={24} sm:size={32} className="text-cm-blue" />
    },
    {
      concern: "Is mijn kind wel veilig online?",
      solution: "100% veilig, geen opslag van gesprekken, volledig AVG-proof en speciaal ontworpen voor kinderen.",
      icon: <Shield size={24} sm:size={32} className="text-cm-green" />
    }
  ];

  const benefits = [
    {
      title: "Emotionele ontwikkeling",
      description: "Uw kind leert gevoelens herkennen, benoemen en er op een gezonde manier mee omgaan.",
      icon: <Heart size={28} sm:size={36} className="text-cm-pink" />
    },
    {
      title: "Zelfvertrouwen opbouwen", 
      description: "Door gesprekken met buddy's groeit het zelfvertrouwen en leren kinderen voor zichzelf opkomen.",
      icon: <CheckCircle size={28} sm:size={36} className="text-cm-green" />
    },
    {
      title: "Sociale vaardigheden",
      description: "Kinderen leren hoe ze vriendschappen sluiten, conflicten oplossen en grenzen stellen.",
      icon: <Users size={28} sm:size={36} className="text-cm-blue" />
    }
  ];

  return (
    <div className="container mx-auto px-4 section-padding overflow-hidden">
      <motion.div
        initial="hidden"
        animate="visible"
        className="text-center mb-12 sm:mb-16"
      >
        <motion.div variants={fadeIn(0,30)} className="flex justify-center mb-6 sm:mb-8">
          <Home size={56} sm:size={72} className="text-cm-pink" />
        </motion.div>
        <motion.h1
          variants={fadeIn(0.1, 30)}
          className="page-title px-2"
        >
          Voor <span className="text-gradient-pink-blue font-medium">Ouders</span>
        </motion.h1>
        <motion.p
          variants={fadeIn(0.2, 20)}
          className="page-subtitle px-2"
        >
          Ontdek hoe Classbuddy uw kind helpt bij sociale en emotionele uitdagingen. Een vertrouwde buddy die altijd luistert, begrijpt en ondersteunt.
        </motion.p>
      </motion.div>

      {/* Common Parent Concerns */}
      <motion.div variants={fadeIn(0.3, 20)} initial="hidden" animate="visible" className="mb-16 sm:mb-20">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-sans font-medium text-cm-blue-dark mb-8 sm:mb-12 text-center px-2">
          Zorgen die alle ouders <span className="text-gradient-pink-blue">herkennen</span>
        </h2>
        <div className="space-y-6 sm:space-y-8">
          {parentConcerns.map((item, index) => (
            <motion.div
              key={index}
              variants={fadeIn(0.4 + index * 0.1, 20)}
              initial="hidden"
              animate="visible"
              className="glassmorphism-card p-6 sm:p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6"
              whileHover={{ y: -5, scale: 1.01 }}
            >
              <div className="flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-cm-pink-light to-white rounded-full flex items-center justify-center shadow-md">
                {item.icon}
              </div>
              <div className="flex-grow">
                <h3 className="text-lg sm:text-xl font-sans font-medium text-cm-blue-dark mb-2 sm:mb-3">"{item.concern}"</h3>
                <p className="text-sm sm:text-base text-cm-gray leading-relaxed">{item.solution}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Benefits for Your Child */}
      <motion.div variants={fadeIn(0.7, 20)} initial="hidden" animate="visible" className="mb-16 sm:mb-20">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-sans font-medium text-cm-blue-dark mb-8 sm:mb-12 text-center px-2">
          Wat heeft uw kind <span className="text-gradient-pink-blue">eraan</span>?
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              variants={fadeIn(0.8 + index * 0.1, 20)}
              initial="hidden"
              animate="visible"
              className="glassmorphism-card p-6 sm:p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 text-center"
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="flex justify-center items-center mb-4 sm:mb-6 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-cm-blue-light to-white rounded-full shadow-md mx-auto">
                {benefit.icon}
              </div>
              <h3 className="text-lg sm:text-xl lg:text-2xl font-sans font-medium text-cm-blue-dark mb-3 sm:mb-4">{benefit.title}</h3>
              <p className="text-sm sm:text-base text-cm-gray leading-relaxed">{benefit.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Privacy & Safety Assurance */}
      <motion.div 
        variants={fadeIn(1.1, 20)} 
        initial="hidden" 
        animate="visible" 
        className="mb-16 sm:mb-20 p-6 sm:p-8 lg:p-10 bg-gradient-to-r from-cm-green-light/30 to-cm-blue-light/30 rounded-2xl sm:rounded-3xl shadow-lg mx-2 sm:mx-0"
      >
        <div className="flex flex-col sm:flex-row items-center justify-center mb-6 sm:mb-8">
          <Lock size={32} sm:size={48} className="text-cm-blue mr-0 sm:mr-4 mb-2 sm:mb-0" />
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-sans font-medium text-cm-blue-dark text-center sm:text-left">
            100% Veilig & <span className="text-gradient-pink-blue">Vertrouwelijk</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 text-center">
          <div className="glassmorphism-card p-4 sm:p-6 rounded-xl">
            <h3 className="text-lg sm:text-xl font-medium text-cm-blue-dark mb-3">Wat we WEL doen</h3>
            <ul className="text-left text-sm sm:text-base text-cm-gray space-y-2">
              <li>✅ Veilige, anonieme gesprekken mogelijk maken</li>
              <li>✅ Kinderen helpen gevoelens te begrijpen</li>
              <li>✅ School informeren bij ernstige zorgen (alleen voornaam)</li>
              <li>✅ Volledig voldoen aan AVG-regelgeving</li>
            </ul>
          </div>
          <div className="glassmorphism-card p-4 sm:p-6 rounded-xl">
            <h3 className="text-lg sm:text-xl font-medium text-cm-blue-dark mb-3">Wat we NOOIT doen</h3>
            <ul className="text-left text-sm sm:text-base text-cm-gray space-y-2">
              <li>❌ Gesprekken opslaan of delen</li>
              <li>❌ Persoonlijke gegevens verzamelen</li>
              <li>❌ Data verkopen aan derden</li>
              <li>❌ Kinderen volgen of monitoren</li>
            </ul>
          </div>
        </div>
      </motion.div>

      {/* Call to Action */}
      <motion.div
        variants={fadeIn(1.4, 20)}
        initial="hidden"
        animate="visible"
        className="p-6 sm:p-8 lg:p-10 bg-gradient-to-r from-cm-pink-light/70 via-cm-yellow-light/70 to-cm-blue-light/70 rounded-2xl sm:rounded-3xl shadow-2xl text-center mx-2 sm:mx-0"
      >
        <Heart size={40} sm:size={56} className="text-cm-pink mx-auto mb-4 sm:mb-6" />
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-sans font-medium text-cm-blue-dark mb-4 sm:mb-5">Geef uw kind een buddy</h2>
        <p className="text-base sm:text-lg text-cm-gray mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
          Elk kind verdient iemand die luistert zonder te oordelen. Vraag uw school om Classbuddy te proberen en geef uw kind de ondersteuning die het verdient.
        </p>
        <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4">
          <Button asChild size="lg" className="bg-gradient-to-r from-cm-pink to-cm-blue hover:from-cm-pink-dark hover:to-cm-blue-dark text-white font-medium text-base sm:text-lg px-6 sm:px-10 py-6 sm:py-7 rounded-xl shadow-xl transform hover:scale-105 transition-all duration-300 group min-h-[48px] w-full sm:w-auto">
            <Link to="/buddys">
              Bekijk de Buddy's <Users className="ml-2 sm:ml-2.5 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="glassmorphism-button text-cm-blue-dark border-cm-blue hover:text-cm-pink hover:border-cm-pink font-medium text-base sm:text-lg px-6 sm:px-10 py-6 sm:py-7 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-300 group min-h-[48px] w-full sm:w-auto">
            <Link to="/contact">
              Stel een vraag <ArrowRightCircle className="ml-2 sm:ml-2.5 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default ParentsPage;
